import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { musicData } from '../data/music';
import { supabase } from '../lib/supabase';

// Fallback aus der lokalen Datei (erste 3), falls Supabase nicht erreichbar ist.
const fallbackPreview = musicData.map((m) => ({ ...m, _image: m.image })).slice(0, 3);

const ConceptsPreview = () => {
  // Erste 3 Titel als Vorschau – zuerst Fallback, dann aus Supabase.
  const [previewItems, setPreviewItems] = useState(fallbackPreview);

  useEffect(() => {
    if (!supabase) return;
    let aktiv = true;
    supabase
      .from('music')
      .select('*')
      .order('sort_order', { ascending: true })
      .limit(3)
      .then(({ data, error }) => {
        if (!aktiv || error || !data || data.length === 0) return;
        setPreviewItems(data.map((m) => ({ ...m, _image: m.image })));
      });
    return () => { aktiv = false; };
  }, []);

  return (
    <section id="konzepte" className="py-24 bg-white dark:bg-navy-950 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xs font-bold tracking-widest text-amber-700 dark:text-amber-400 uppercase mb-3">
            Ausgewählte Werke
          </p>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 dark:text-stone-100 mb-4">
            Musik & Song-Konzepte
          </h2>
          <p className="text-stone-500 dark:text-stone-400 max-w-xl mx-auto">
            Lyrik in Musik transferiert – als Anregung, Ausgangspunkt und Einladung zum Weiterdenken, Diskutieren, Entwickeln.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {previewItems.map((item, i) => (
            <motion.div
              key={`preview-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="group relative overflow-hidden rounded-sm shadow-md hover:shadow-xl transition-shadow duration-300 bg-stone-50 dark:bg-navy-900"
            >
              <div className="aspect-[3/2] overflow-hidden">
                {item._image ? (
                  <img
                    src={item._image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-brand dark:bg-navy-800" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>
              <div className="p-6">
                <p className="text-xs font-bold text-amber-700 dark:text-amber-400 tracking-widest uppercase mb-1">
                  {item.poet}
                </p>
                <h3 className="text-xl font-serif font-bold text-stone-900 dark:text-stone-100 mb-2">
                  {item.title}
                </h3>
                <p className="text-stone-500 dark:text-stone-400 text-sm">
                  {item.style || item.language || ''}
                </p>
                <span className={`inline-block mt-3 text-xs px-2 py-1 rounded-full font-semibold ${
                  item.status === 'Demo'
                    ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300'
                    : 'bg-stone-100 dark:bg-navy-800 text-stone-600 dark:text-stone-400'
                }`}>
                  {item.status}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/musik"
            className="inline-block bg-brand dark:bg-brand-light text-white px-8 py-3 rounded-sm font-semibold hover:bg-brand-dark dark:hover:bg-brand transition-colors duration-300"
          >
            Alle Werke ansehen
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ConceptsPreview;
